import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatDate } from '../../../../helpers/date-time-helpers';
import { skyBase } from '../../../../styles/design-tokens';

dayjs.extend(utc);

/**
 * Java's String.hashCode() equivalent for consistent string-to-number conversion
 *
 * @param str - The string to hash
 * @returns A positive integer hash value
 */
function hashCode(str: string): number {
  let hash = 0;
  if (str.length === 0) return hash;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash);
}

/**
 * Assigns unique colors to selected items while ensuring no conflicts
 *
 * This function ensures that:
 * - Each selected item gets a unique color
 * - Items maintain consistent color preferences when possible
 * - Colors are distributed efficiently without conflicts
 * - The same item will generally get the same color across selections
 *
 * @param selectedItemIds - Array of item IDs that need color assignments
 * @param colorsList - Array of available colors to assign from
 * @returns Map containing itemId -> color assignments
 *
 * @example
 * ```typescript
 * const assignments = assignColorsToItems(
 *   ['item1', 'item2', 'item3'],
 *   ['#000000', '#111111', '#222222']
 * );
 * // Returns: Map { 'item1' => '#000000', 'item2' => '#111111', 'item3' => '#222222' }
 * ```
 */
function assignColorsToItems(selectedItemIds: string[], colorsList: readonly string[]): Map<string, string> {
  const assignments = new Map<string, string>();
  const usedColors = new Set<string>();

  // Sort items to ensure deterministic assignment order
  // This prevents color assignments from changing based on selection order
  const sortedItems = [...selectedItemIds].sort();

  sortedItems.forEach((itemId) => {
    // Try to use hash-based preferred color first
    // This gives items a consistent "preferred" color when possible
    const hashIndex = hashCode(itemId) % colorsList.length;
    const preferredColor = colorsList[hashIndex];

    if (!usedColors.has(preferredColor)) {
      assignments.set(itemId, preferredColor);
      usedColors.add(preferredColor);
    } else {
      // Find next available color in order
      // This ensures we don't have color conflicts
      for (const color of colorsList) {
        if (!usedColors.has(color)) {
          assignments.set(itemId, color);
          usedColors.add(color);
          break;
        }
      }
    }
  });

  return assignments;
}

/**
 * Gets the assigned color for a specific item
 *
 * @param itemId - The ID of the item to get the color for
 * @param colorAssignments - Map of item IDs to their assigned colors
 * @param fallbackColor - Color to return if item is not found (default: skyBase)
 * @returns The assigned color or fallback color
 *
 * @example
 * ```typescript
 * const color = getItemColor('item1', colorAssignments, '#cccccc');
 * // Returns the assigned color for 'item1' or '#cccccc' if not found
 * ```
 */
function getItemColor(itemId: string, colorAssignments: Map<string, string>, fallbackColor: string = skyBase): string {
  return colorAssignments.get(itemId) || fallbackColor;
}

/**
 * Formats X-axis labels for charts - uses sprint label if present, otherwise formats the date.
 * This enables charts to display sprint names when in "By Sprint" mode and month/year when in "By Date" mode.
 * Handles date strings in a timezone-agnostic way to prevent different displays across timezones.
 *
 * @param value - The value to format (can be Date, string label, or timestamp)
 * @param _index - Index parameter (required by Recharts but not used)
 * @returns Formatted string for X-axis display
 *
 * @example
 * ```typescript
 * // With sprint label (string that doesn't parse as date)
 * formatXAxisLabel('Sprint 1')
 * // Returns: 'Sprint 1'
 *
 * // With date string (YYYY-MM format from backend)
 * formatXAxisLabel('2025-05')
 * // Returns: 'May 25' (consistent across all timezones)
 *
 * // With date object
 * formatXAxisLabel(new Date(2024, 0, 1))
 * // Returns: 'Jan 24'
 * ```
 */
function formatXAxisLabel(value: any, _index: number): string {
  const isDateString = typeof value === 'string' && /^\d{4}-\d{2}/.test(value);

  // If value is a string that doesn't match date pattern, treat as sprint label
  if (typeof value === 'string' && !isDateString) {
    return value;
  }

  // For date strings in YYYY-MM or YYYY-MM-DD format, parse in UTC to avoid timezone shifts
  // This ensures all users see the same month regardless of their timezone
  if (isDateString) {
    // Parse as UTC to prevent timezone conversion issues
    // E.g., "2025-05" will always show as "May 25" for everyone
    const date = dayjs.utc(value);
    if (!date.isValid()) {
      return value;
    }
    // Use formatDate helper with UTC input to maintain timezone consistency
    return formatDate(date, { format: 'MMM YY', utcInput: true });
  }

  // For Date objects, timestamps, and other types, format using the helper
  return formatDate(value, { format: 'MMM YY', fallback: String(value) });
}

export { assignColorsToItems, formatXAxisLabel, getItemColor };
